import { useState } from "react";
import type { TurnEntry } from "../../api/calls";

/**
 * One turn in the call transcript. Dense, technical, readable at a
 * glance: turn number badge, timestamp, speaker-tinted text, and a
 * latency / token metrics strip at the bottom. Error tinting on the
 * left edge when telemetry failed for this turn.
 */
export function TurnRow({ turn }: { turn: TurnEntry }) {
  const started = new Date(turn.started_at);
  const hasErrors = (turn.errors ?? []).length > 0;
  const latencies = turn.latencies_ms ?? {};
  const tokens = turn.tokens ?? {};
  const inputTokens = (tokens.input_tokens as number | undefined) ?? 0;
  const outputTokens = (tokens.output_tokens as number | undefined) ?? 0;
  const [showTimings, setShowTimings] = useState(false);
  const metricCount = Object.keys(latencies).length + (inputTokens || outputTokens ? 1 : 0);

  return (
    <div
      style={{
        padding: "14px 18px",
        borderBottom: "1px solid var(--border-subtle)",
        borderLeft: hasErrors ? "2px solid var(--error)" : "2px solid transparent",
        background: hasErrors ? "rgba(244,63,94,0.025)" : "transparent",
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.12em",
          color: "var(--text-muted)",
          marginBottom: 10,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "2px 8px",
            border: "1px solid var(--accent-line)",
            background: "var(--accent-subtle)",
            color: "var(--accent)",
            fontSize: 9.5,
            fontWeight: 500,
            letterSpacing: "0.14em",
          }}
        >
          T{String(turn.turn_index).padStart(2, "0")}
        </span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {started.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </span>
        {hasErrors ? (
          <span
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--error)",
              fontSize: 9.5,
              letterSpacing: "0.14em",
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "var(--error)",
                boxShadow: "var(--glow-error)",
              }}
            />
            {turn.errors!.length} ERROR{turn.errors!.length === 1 ? "" : "S"}
          </span>
        ) : null}
      </div>

      {/* Speaker lines */}
      {turn.user_text ? <SpeakerLine speaker="target" text={turn.user_text} /> : null}
      {turn.bot_text ? <SpeakerLine speaker="persona" text={turn.bot_text} /> : null}

      {/* Metrics strip — collapsed by default */}
      {metricCount > 0 ? (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowTimings((s) => !s)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              letterSpacing: "0.12em",
              color: "var(--text-muted)",
            }}
          >
            {showTimings ? "▾" : "▸"} timings ({metricCount})
          </button>
          {showTimings ? (
            <div
              style={{
                marginTop: 8,
                paddingTop: 10,
                borderTop: "1px dashed var(--border-subtle)",
                display: "flex",
                flexWrap: "wrap",
                gap: "8px 16px",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.04em",
                color: "var(--text-muted)",
              }}
            >
              {Object.entries(latencies).map(([k, v]) => (
                <Metric key={k} label={k} value={`${v}ms`} />
              ))}
              {inputTokens || outputTokens ? (
                <Metric label="tokens" value={`${inputTokens}/${outputTokens}`} />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          color: "var(--text-muted)",
          fontSize: 9.5,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: "var(--text-secondary)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </span>
  );
}

function SpeakerLine({
  speaker,
  text,
}: {
  speaker: "target" | "persona";
  text: string;
}) {
  const isPersona = speaker === "persona";
  const accent = isPersona ? "var(--accent)" : "var(--cta)";
  const label = isPersona ? "PERSONA" : "TARGET";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "84px 1fr",
        gap: 14,
        padding: "5px 0",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: accent,
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          paddingTop: 3,
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 2,
            height: 12,
            background: accent,
            marginTop: 2,
            flexShrink: 0,
          }}
        />
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.55,
          color: isPersona ? "var(--text-primary)" : "var(--text-secondary)",
          letterSpacing: "-0.005em",
        }}
      >
        {text}
      </div>
    </div>
  );
}
